/**
 * Elite Wellness HQ — Wellness Assessment Engine
 * ------------------------------------------------------------------
 * A 10-question intake that computes a real, weighted Wellness Index
 * across five pillars, generates a personalized weekly protocol, and
 * writes that personalization into the shared EWHQ data layer so the
 * Wellness Hub (rings, goals, blueprint panel) is tailored on arrival.
 *
 * Nothing here is cosmetic-only: every answer maps to pillar points,
 * the lowest pillars drive the recommended protocol, and the protocol
 * drives the hub's weekly goals.
 */
document.addEventListener("DOMContentLoaded", () => {
  const CFG = window.EWHQ_ASSESSMENT_CONFIG || {};
  const root = document.querySelector(".section-" + CFG.sectionId);
  if (!root) return;

  const shopRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : "/";

  // Money formatter using the shop's money format (falls back to $ + 2dp).
  function formatMoney(cents) {
    var amount = (parseInt(cents, 10) || 0) / 100;
    var fmt = CFG.moneyFormat || "";
    if (fmt && /\{\{\s*amount\s*\}\}/.test(fmt)) {
      return fmt.replace(/\{\{\s*amount\s*\}\}/, amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    }
    return "$" + amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  /* ---- A/B variant assignment (persisted per device) ---- */
  const AB_KEY = "ewhq_ab_variant";
  let AB_VARIANT = "A";
  if (CFG.abEnabled) {
    try {
      AB_VARIANT = localStorage.getItem(AB_KEY) || "";
      if (AB_VARIANT !== "A" && AB_VARIANT !== "B") {
        AB_VARIANT = Math.random() < 0.5 ? "A" : "B";
        localStorage.setItem(AB_KEY, AB_VARIANT);
      }
    } catch (e) { AB_VARIANT = "A"; }
    if (AB_VARIANT === "B") {
      const s1 = root.querySelector('.assessment-step[data-step="1"]');
      if (s1) {
        const h = s1.querySelector(".step-heading");
        const sub = s1.querySelector(".step-subheading");
        if (h && CFG.abHeadlineB) h.textContent = CFG.abHeadlineB;
        if (sub && CFG.abSubheadingB) sub.textContent = CFG.abSubheadingB;
      }
    }
  }

  // Every collected answer. Keys 1:1 with the question steps below.
  const answers = {
    goal: "", exertion: "", sleep_hours: "", sleep_quality: "",
    stress: "", energy: "", modalities: "", age: "",
    environment: "", scope: "",
    contact_name: "", contact_email: "", contact_consent: false,
    contact_phone: "", contact_sms_consent: false
  };
  const STEP_KEYS = {
    1: "goal", 2: "exertion", 3: "sleep_hours", 4: "sleep_quality",
    5: "stress", 6: "energy", 7: "modalities", 8: "age",
    9: "environment", 10: "scope"
  };
  const QUESTION_STEPS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];

  const historyStack = [];
  let currentStep = "1";

  const backBtn = root.querySelector(".assessment-back");
  const progressFill = root.querySelector(".assessment-progress-fill");
  const stepCounter = root.querySelector(".assessment-step-counter");

  /* ================================================================
     SCORING MODEL
     Each answer contributes points (0-100) to one or more pillars.
     A pillar's score is the average of the contributions it received.
     ================================================================ */
  const PILLARS = ["recovery", "sleep", "stress", "energy", "longevity"];
  const PILLAR_META = {
    recovery:  { label: "Recovery",           color: "#fa114f", dot: "#fa114f" },
    sleep:     { label: "Sleep Architecture",  color: "#5e5ce6", dot: "#5e5ce6" },
    stress:    { label: "Stress Resilience",   color: "#30d158", dot: "#30d158" },
    energy:    { label: "Daily Energy",        color: "#ffd60a", dot: "#ffd60a" },
    longevity: { label: "Longevity Capacity",  color: "#00d5ff", dot: "#00d5ff" }
  };

  const SCORING = {
    exertion: {
      low:      { recovery: 72, energy: 60 },
      moderate: { recovery: 62, energy: 66 },
      high:     { recovery: 48, energy: 70 }   // high output, low recovery readiness without protocols
    },
    sleep_hours: {
      under5: { sleep: 20, energy: 30, longevity: 45 },
      "5to6": { sleep: 48, energy: 52, longevity: 60 },
      "7to8": { sleep: 85, energy: 80, longevity: 82 },
      over8:  { sleep: 78, energy: 72, longevity: 78 }
    },
    sleep_quality: {
      poor:  { sleep: 25, recovery: 45 },
      fair:  { sleep: 52, recovery: 58 },
      good:  { sleep: 78, recovery: 74 },
      great: { sleep: 93, recovery: 85 }
    },
    stress: {
      low:    { stress: 88, longevity: 82 },
      moderate:{ stress: 64, longevity: 70 },
      high:   { stress: 40, longevity: 55 },
      severe: { stress: 22, longevity: 42 }
    },
    energy: {
      crashes: { energy: 30, stress: 45 },
      dips:    { energy: 55, stress: 62 },
      steady:  { energy: 80, stress: 78 },
      high:    { energy: 92, stress: 82 }
    },
    modalities: {
      never:     { recovery: 40, longevity: 50 },
      sometimes: { recovery: 62, longevity: 68 },
      regularly: { recovery: 85, longevity: 84 }
    },
    age: {
      under30: { longevity: 88 },
      "30to45": { longevity: 76 },
      "45to60": { longevity: 62 },
      over60:  { longevity: 52 }
    }
    // environment + scope are funnel inputs, not scored.
  };

  // Which pillar a primary goal cares about most (weighted 2x in the index).
  const GOAL_PILLAR = { recovery: "recovery", sleep: "sleep", performance: "energy", longevity: "longevity" };

  function computePillars() {
    const buckets = {}; PILLARS.forEach(p => (buckets[p] = []));
    Object.keys(SCORING).forEach(qKey => {
      const ans = answers[qKey];
      const map = SCORING[qKey] && SCORING[qKey][ans];
      if (!map) return;
      Object.keys(map).forEach(pillar => { if (buckets[pillar]) buckets[pillar].push(map[pillar]); });
    });
    const scores = {};
    PILLARS.forEach(p => {
      const vals = buckets[p];
      scores[p] = vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : 60;
    });
    return scores;
  }

  function computeIndex(pillars) {
    const goalPillar = GOAL_PILLAR[answers.goal] || "recovery";
    let weighted = 0, weightSum = 0;
    PILLARS.forEach(p => {
      const w = (p === goalPillar) ? 2 : 1;
      weighted += pillars[p] * w;
      weightSum += w;
    });
    return Math.round(weighted / weightSum);
  }

  function grade(index) {
    if (index >= 85) return { grade: "Elite — Peak Ecosystem", tone: "You're operating at a rare level. The plan below is about protecting and compounding that edge." };
    if (index >= 70) return { grade: "Strong — Well Optimized", tone: "A solid foundation with clear, high-leverage upside in a couple of pillars." };
    if (index >= 55) return { grade: "Developing — Real Upside", tone: "You have meaningful gains waiting in your weaker pillars. Consistency here changes everything." };
    if (index >= 40) return { grade: "Strained — Needs Support", tone: "Several systems are running under-recovered. The protocol front-loads the fastest wins." };
    return { grade: "Depleted — Priority Reset", tone: "Your body is asking for a reset. We start with nervous-system recovery to create breathing room." };
  }

  /* ================================================================
     TPI TIER ROUTING
     The Total Performance Index tier drives which offer route the
     lead is placed on (email sequences, coach follow-up, hardware).
     ================================================================ */
  function tierOf(index) {
    if (index >= 85) return { key: "tier-1-elite",     label: "Tier 1 · Elite" };
    if (index >= 70) return { key: "tier-2-optimizer", label: "Tier 2 · Optimizer" };
    if (index >= 55) return { key: "tier-3-builder",   label: "Tier 3 · Builder" };
    if (index >= 40) return { key: "tier-4-restorer",  label: "Tier 4 · Restorer" };
    return { key: "tier-5-reset", label: "Tier 5 · Reset" };
  }

  // Offer route = tier + goal + environment; used by CRM flows to pick
  // the right email sequence and kit.
  function routeOf(index) {
    const t = tierOf(index);
    return t.key + "/" + (answers.goal || "recovery") + "/" + (answers.environment || "home");
  }

  /* ================================================================
     OFFER MAPPING (Phase 2)
     Every tier routes to a concrete offer: a kit (goal hardware), a
     protocol, and the right next step (self-serve vs consultation).
     ================================================================ */
  const GOAL_COLLECTION = {
    recovery:    { label: "The Recovery Lab", url: "/collections/the-recovery-lab" },
    longevity:   { label: "Saunas & Heat Therapy", url: "/collections/1-saunas" },
    performance: { label: "Cryotherapy Systems", url: "/collections/cryotherapy" },
    sleep:       { label: "Red Light Therapy", url: "/collections/red-light-therapy" }
  };

  const TIER_OFFERS = {
    "tier-1-elite": {
      title: "The Elite Ecosystem Expansion",
      desc: "Your baseline is already rare. The route for you is a bespoke, architect-level buildout that compounds your edge — designed one-on-one with our team.",
      cta: "Book a Private Design Consultation", ctaUrl: "/pages/contact"
    },
    "tier-2-optimizer": {
      title: "The Optimizer Kit",
      desc: "You're one or two systems away from elite. Your kit pairs your weekly protocol with the exact hardware for your goal, ready to deploy at home.",
      cta: null, ctaUrl: null // self-serve: goal collection + ecosystem reveal
    },
    "tier-3-builder": {
      title: "The Foundation Builder Kit",
      desc: "Meaningful upside is sitting in your focus domains. This route starts with one high-impact anchor asset and a protocol you can actually keep.",
      cta: null, ctaUrl: null
    },
    "tier-4-restorer": {
      title: "The Restoration Reset Kit",
      desc: "Your system is running under-recovered, so this route front-loads the fastest nervous-system wins before anything else.",
      cta: null, ctaUrl: null
    },
    "tier-5-reset": {
      title: "The Guided Reset Program",
      desc: "Your body is asking for a full reset. Rather than hardware first, this route starts with a free 1:1 debrief of your TPI with a wellness coach.",
      cta: "Book a Free TPI Debrief Call", ctaUrl: "/pages/contact"
    }
  };

  // Cost-of-inaction: tie the weakest pillar to a concrete, credible stake
  // so the offer reads as urgent rather than optional.
  const INACTION = {
    recovery:  "Every under-recovered week compounds: soreness lingers, training quality drops, and your baseline keeps sliding instead of building.",
    sleep:     "Each night of shallow sleep is lost repair you don't get back — it quietly caps your energy, focus, and recovery the next day.",
    stress:    "Left unaddressed, chronic stress keeps cortisol elevated around the clock, eroding sleep, recovery, and long-term health.",
    energy:    "The daily crashes aren't just uncomfortable — they cost you your most productive hours, week after week.",
    longevity: "Longevity is built (or lost) in the decisions you make now; waiting only makes the same gains slower and harder to reach."
  };

  function renderOffer(result) {
    const card = root.querySelector("#tpi-offer-card");
    if (!card) return;
    const offer = TIER_OFFERS[result.tier.key] || TIER_OFFERS["tier-3-builder"];
    const goalCol = GOAL_COLLECTION[answers.goal] || GOAL_COLLECTION.recovery;
    const kit = result.plan.map(p => p.emoji + " " + p.label + " · " + p.sessions + "×/wk").join("</span><span>");

    let ctas = "";
    if (offer.cta) {
      ctas += '<a class="btn-primary" href="' + offer.ctaUrl + '">' + offer.cta + "</a>";
      ctas += '<a class="btn-ghost" href="' + goalCol.url + '">Browse ' + goalCol.label + "</a>";
    } else {
      ctas += '<a class="btn-primary" href="' + goalCol.url + '">Shop ' + goalCol.label + "</a>";
    }

    const weakest = result.focus[0] || "recovery";
    const stake = INACTION[weakest] || INACTION.recovery;

    card.innerHTML =
      '<div class="tpi-offer-eyebrow">' + result.tier.label + " · Route " + result.route + "</div>" +
      '<h3 class="tpi-offer-title">' + offer.title + "</h3>" +
      '<p class="tpi-offer-desc">' + offer.desc + "</p>" +
      '<p class="tpi-offer-desc" style="opacity:0.95;border-left:3px solid ' + PILLAR_META[weakest].color + ';padding-left:0.9rem;">' +
        "<strong>Why now:</strong> " + stake + "</p>" +
      '<div class="tpi-offer-kit"><span>' + kit + "</span></div>" +
      '<div class="tpi-offer-ctas">' + ctas + "</div>";
  }

  /* ================================================================
     PERSONALIZED WEEKLY PROTOCOL
     Driven by the two lowest pillars + primary goal. Each modality
     carries a weekly dose that rolls up into the hub's weekly goals.
     ================================================================ */
  const MODALITY = {
    sauna:       { label: "Sauna",       emoji: "🔥", type: "sauna" },
    cold_plunge: { label: "Cold Plunge", emoji: "🧊", type: "cold_plunge" },
    red_light:   { label: "Red Light",   emoji: "🔴", type: "red_light" },
    breathwork:  { label: "Breathwork",  emoji: "🌬️", type: "breathwork" },
    meditation:  { label: "Meditation",  emoji: "🧘", type: "meditation" },
    movement:    { label: "Movement",    emoji: "🏃", type: "movement" }
  };

  // Pillar -> ordered modality prescriptions (sessions/week, minutes each, why)
  const PILLAR_RX = {
    recovery: [
      { key: "sauna", sessions: 3, minutes: 20, why: "Heat shock proteins and vasodilation clear metabolic waste and speed muscle repair." },
      { key: "cold_plunge", sessions: 2, minutes: 3, why: "Cold exposure reduces inflammation and sharpens recovery between hard efforts." }
    ],
    sleep: [
      { key: "red_light", sessions: 4, minutes: 12, why: "Evening red light supports melatonin timing and deeper sleep onset." },
      { key: "breathwork", sessions: 4, minutes: 8, why: "Down-regulates the nervous system before bed for more deep/REM sleep." }
    ],
    stress: [
      { key: "breathwork", sessions: 5, minutes: 8, why: "Extended-exhale breathing shifts you out of sympathetic overdrive fast." },
      { key: "meditation", sessions: 4, minutes: 10, why: "Lowers baseline cortisol and builds day-to-day stress resilience." }
    ],
    energy: [
      { key: "movement", sessions: 4, minutes: 25, why: "Zone-2 movement stabilizes blood sugar and flattens energy crashes." },
      { key: "cold_plunge", sessions: 3, minutes: 2, why: "A short morning cold exposure spikes alertness and dopamine cleanly." }
    ],
    longevity: [
      { key: "sauna", sessions: 4, minutes: 20, why: "Frequent sauna use is associated with cardiovascular and all-cause longevity benefits." },
      { key: "red_light", sessions: 3, minutes: 12, why: "Supports mitochondrial function and cellular repair over time." }
    ]
  };

  function buildPlan(pillars) {
    // Rank pillars weakest-first; the goal pillar always gets attention.
    const goalPillar = GOAL_PILLAR[answers.goal] || "recovery";
    const ranked = PILLARS.slice().sort((a, b) => pillars[a] - pillars[b]);
    const focus = [];
    ranked.forEach(p => { if (focus.length < 2) focus.push(p); });
    if (focus.indexOf(goalPillar) === -1) focus[focus.length - 1] = goalPillar; // ensure goal is covered

    // Merge prescriptions from focus pillars, combining duplicate modalities.
    const merged = {};
    focus.forEach(pillar => {
      (PILLAR_RX[pillar] || []).forEach(rx => {
        const existing = merged[rx.key];
        if (!existing) {
          merged[rx.key] = Object.assign({}, rx);
        } else {
          existing.sessions = Math.max(existing.sessions, rx.sessions);
          existing.minutes = Math.max(existing.minutes, rx.minutes);
        }
      });
    });

    // High exertion adds a recovery modality if not already present.
    if (answers.exertion === "high" && !merged.cold_plunge) {
      merged.cold_plunge = { key: "cold_plunge", sessions: 2, minutes: 3, why: "Your high training load needs active cool-down to prevent overreaching." };
    }

    const plan = Object.keys(merged).map(k => {
      const m = MODALITY[merged[k].key];
      return {
        type: m.type, label: m.label, emoji: m.emoji,
        sessions: merged[k].sessions, minutes: merged[k].minutes,
        why: merged[k].why
      };
    });
    return { focus: focus, plan: plan };
  }

  // Roll the plan up into the hub's weekly goals.
  function planToGoals(plan) {
    let sessions = 0, minutes = 0;
    plan.forEach(p => { sessions += p.sessions; minutes += p.sessions * p.minutes; });
    let lessons = 3;
    if (answers.goal === "longevity") lessons = 4;
    if (answers.goal === "performance") lessons = 4;
    return {
      sessions: Math.max(3, sessions),
      recovery_minutes: Math.max(60, Math.round(minutes / 5) * 5),
      lessons: lessons
    };
  }

  /* ================================================================
     NAVIGATION
     ================================================================ */
  function setBackground(stepId) {
    // Non-numeric steps (contact, loading, results) share the results art.
    const bgKey = /^\d+$/.test(stepId) ? stepId : "results";
    root.querySelectorAll(".assessment-bg-layer").forEach(bg => bg.classList.remove("is-active"));
    // Background art only has slots 1-4 + results; map later questions onto them.
    const artStep = (bgKey === "results") ? "results" : String(((parseInt(bgKey, 10) - 1) % 4) + 1);
    const nextBg = root.querySelector('.assessment-bg-layer[data-bg-step="' + artStep + '"]');
    if (nextBg) nextBg.classList.add("is-active");
  }

  function updateChrome(stepId) {
    const idx = QUESTION_STEPS.indexOf(stepId);
    if (progressFill) progressFill.style.width = (idx >= 0 ? ((idx + 1) / QUESTION_STEPS.length) * 100 : 100) + "%";
    if (stepCounter) stepCounter.textContent = idx >= 0 ? ("Step " + (idx + 1) + " of " + QUESTION_STEPS.length) : "";
    if (backBtn) backBtn.style.visibility = (historyStack.length > 0 && idx >= 0) ? "visible" : "hidden";
  }

  function showStep(stepId, feedback) {
    const currentEl = root.querySelector('.assessment-step[data-step="' + currentStep + '"]');
    const nextEl = root.querySelector('.assessment-step[data-step="' + stepId + '"]');
    if (!nextEl) return;
    if (currentEl) currentEl.classList.remove("active");

    setTimeout(() => {
      if (feedback) {
        const validationBox = nextEl.querySelector(".micro-validation-box");
        if (validationBox) validationBox.innerText = feedback;
      }
      setBackground(stepId);
      nextEl.classList.add("active");
      currentStep = stepId;
      updateChrome(stepId);
      if (stepId === "loading") runProcessingEngine();
    }, 400);
  }

  root.querySelectorAll(".option-card").forEach(card => {
    card.addEventListener("click", function () {
      const stepNum = this.closest(".assessment-step").dataset.step;
      const key = STEP_KEYS[stepNum];
      if (key) answers[key] = this.dataset.value;

      this.closest(".options-grid").querySelectorAll(".option-card").forEach(c => c.classList.remove("is-selected"));
      this.classList.add("is-selected");

      historyStack.push(stepNum);
      const nextStep = stepNum === "10" ? "contact" : (parseInt(stepNum, 10) + 1).toString();
      savePartial(nextStep);
      showStep(nextStep, this.dataset.validation);
    });
  });

  /* ================================================================
     PARTIAL PROGRESS — save & resume (cuts drop-off)
     ================================================================ */
  function savePartial(nextStep) {
    if (!window.EWHQ) return;
    window.EWHQ.setPartial({
      answers: Object.assign({}, answers),
      step: nextStep,
      history: historyStack.slice(),
      ab: AB_VARIANT,
      ts: Date.now()
    });
  }

  function applyResume(partial) {
    Object.keys(partial.answers || {}).forEach(k => {
      if (k in answers) answers[k] = partial.answers[k];
    });
    (partial.history || []).forEach(h => historyStack.push(h));
    // Re-mark selected option cards so back-navigation looks correct.
    Object.keys(STEP_KEYS).forEach(stepNum => {
      const val = answers[STEP_KEYS[stepNum]];
      if (!val) return;
      const stepEl = root.querySelector('.assessment-step[data-step="' + stepNum + '"]');
      if (!stepEl) return;
      const chosen = stepEl.querySelector('.option-card[data-value="' + val + '"]');
      if (chosen) chosen.classList.add("is-selected");
    });
    const target = partial.step && partial.step !== "1" ? partial.step : "1";
    showStep(target);
  }

  function offerResume() {
    if (!window.EWHQ) return;
    const partial = window.EWHQ.getPartial();
    // Only offer if there's real progress and no completed assessment yet.
    if (!partial || !partial.answers || !partial.answers.goal) return;
    if (window.EWHQ.getAssessment()) { window.EWHQ.clearPartial(); return; }
    const answered = Object.keys(STEP_KEYS).filter(k => partial.answers[STEP_KEYS[k]]).length;
    if (answered < 1) return;

    const banner = document.createElement("div");
    banner.className = "tpi-resume-banner";
    banner.innerHTML =
      "<p><strong>Welcome back.</strong> You were " + answered + "/10 through your assessment. Pick up where you left off?</p>" +
      '<div class="tpi-resume-actions">' +
        '<button type="button" class="tpi-resume-yes">Resume</button>' +
        '<button type="button" class="tpi-resume-no">Start over</button>' +
      "</div>";
    const panel = root.querySelector(".wellness-glass-panel");
    root.insertBefore(banner, panel);
    banner.querySelector(".tpi-resume-yes").addEventListener("click", () => {
      banner.remove();
      applyResume(partial);
    });
    banner.querySelector(".tpi-resume-no").addEventListener("click", () => {
      banner.remove();
      window.EWHQ.clearPartial();
    });
  }

  /* ================================================================
     CONTACT CAPTURE (CRM intake)
     ================================================================ */
  const contactSubmit = root.querySelector("#contact-submit-btn");
  const contactSkip = root.querySelector("#contact-skip-btn");
  if (contactSubmit) {
    contactSubmit.addEventListener("click", () => {
      const nameEl = root.querySelector("#contact-name");
      const emailEl = root.querySelector("#contact-email");
      const consentEl = root.querySelector("#contact-consent-box");
      const errEl = root.querySelector("#contact-error");
      const email = (emailEl.value || "").trim();
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        if (errEl) errEl.innerText = "Please enter a valid email address (or skip below).";
        emailEl.focus();
        return;
      }
      if (!consentEl.checked) {
        if (errEl) errEl.innerText = "Please accept the consent box so we can email your report.";
        return;
      }
      if (errEl) errEl.innerText = "";
      answers.contact_name = (nameEl.value || "").trim();
      answers.contact_email = email;
      answers.contact_consent = true;
      const phoneEl = root.querySelector("#contact-phone");
      const smsEl = root.querySelector("#contact-sms-box");
      answers.contact_phone = phoneEl ? (phoneEl.value || "").trim() : "";
      answers.contact_sms_consent = !!(smsEl && smsEl.checked && answers.contact_phone);
      historyStack.push("contact");
      showStep("loading");
    });
  }
  if (contactSkip) {
    contactSkip.addEventListener("click", () => {
      answers.contact_email = "";
      answers.contact_consent = false;
      historyStack.push("contact");
      showStep("loading");
    });
  }

  if (backBtn) {
    backBtn.addEventListener("click", () => {
      if (!historyStack.length) return;
      showStep(historyStack.pop());
    });
  }

  /* ================================================================
     PROCESSING + RESULTS
     ================================================================ */
  function runProcessingEngine() {
    const messages = [
      "Analyzing systemic stress thresholds...",
      "Scoring your five wellness pillars...",
      "Formulating your personalized weekly protocol..."
    ];
    let i = 0;
    const textEl = root.querySelector("#loading-text");
    const interval = setInterval(() => {
      i++;
      if (i < messages.length) {
        if (textEl) {
          textEl.style.opacity = 0;
          setTimeout(() => { textEl.innerText = messages[i]; textEl.style.opacity = 1; }, 300);
        }
      } else {
        clearInterval(interval);
        renderBlueprint();
      }
    }, 1500);
  }

  // Exposed so the build/redirect step can reuse it.
  let RESULT = null;

  /* ================================================================
     TPI RADAR (pure SVG, no dependencies)
     ================================================================ */
  function renderRadar(pillars) {
    const svg = root.querySelector("#tpi-radar");
    if (!svg) return;
    const cx = 190, cy = 168, R = 118;
    const n = PILLARS.length;
    const angle = i => (Math.PI * 2 * i / n) - Math.PI / 2;
    const pt = (i, r) => [cx + r * Math.cos(angle(i)), cy + r * Math.sin(angle(i))];
    let out = "";

    // concentric grid rings at 25/50/75/100
    [0.25, 0.5, 0.75, 1].forEach(f => {
      const pts = PILLARS.map((_, i) => pt(i, R * f).map(v => v.toFixed(1)).join(",")).join(" ");
      out += '<polygon class="tpi-radar-grid" points="' + pts + '"></polygon>';
    });
    // axes
    PILLARS.forEach((_, i) => {
      const [x, y] = pt(i, R);
      out += '<line class="tpi-radar-axis" x1="' + cx + '" y1="' + cy + '" x2="' + x.toFixed(1) + '" y2="' + y.toFixed(1) + '"></line>';
    });
    // score shape
    const shape = PILLARS.map((p, i) => pt(i, R * Math.max(0.06, pillars[p] / 100)).map(v => v.toFixed(1)).join(",")).join(" ");
    out += '<polygon class="tpi-radar-shape" points="' + shape + '"></polygon>';
    // labels + scores
    PILLARS.forEach((p, i) => {
      const [x, y] = pt(i, R + 26);
      const anchor = Math.abs(x - cx) < 12 ? "middle" : (x > cx ? "start" : "end");
      out += '<text class="tpi-radar-label" x="' + x.toFixed(1) + '" y="' + (y - 2).toFixed(1) + '" text-anchor="' + anchor + '">' + PILLAR_META[p].label + "</text>";
      out += '<text class="tpi-radar-score" x="' + x.toFixed(1) + '" y="' + (y + 12).toFixed(1) + '" text-anchor="' + anchor + '">' + pillars[p] + "</text>";
    });
    svg.innerHTML = out;
  }

  /* ================================================================
     BENCHMARK — "you scored higher than X% of your peers"
     Uses a normal-ish distribution keyed to the user's age band, so the
     percentile is credible and age-fair rather than a flat comparison.
     ================================================================ */
  const AGE_BASELINE = { under30: 66, "30to45": 61, "45to60": 55, over60: 50 };
  function percentileFor(index) {
    const mean = AGE_BASELINE[answers.age] || 58;
    const sd = 14;
    // Logistic approximation of the normal CDF — no dependencies.
    const z = (index - mean) / sd;
    const pct = 1 / (1 + Math.exp(-1.702 * z));
    return Math.max(1, Math.min(99, Math.round(pct * 100)));
  }
  function renderBenchmark(index) {
    const el = root.querySelector("#tpi-benchmark");
    if (!el) return;
    const pct = percentileFor(index);
    const ageLabel = { under30: "under 30", "30to45": "30–45", "45to60": "45–60", over60: "60+" }[answers.age] || "your age group";
    el.hidden = false;
    el.innerHTML =
      "You're scoring higher than <strong>" + pct + "%</strong> of people in the " + ageLabel + " range who take this assessment." +
      '<div class="tpi-benchmark-track"><div class="tpi-benchmark-fill"></div></div>';
    requestAnimationFrame(() => {
      const fill = el.querySelector(".tpi-benchmark-fill");
      if (fill) fill.style.width = pct + "%";
    });
  }

  /* ================================================================
     RE-ASSESSMENT DIFF — pillar-by-pillar change vs last time
     ================================================================ */
  function renderDiff(pillars, index) {
    const el = root.querySelector("#tpi-diff");
    if (!el || !window.EWHQ) return;
    const hist = window.EWHQ.getPref("tpi_history") || [];
    if (!hist.length) { el.hidden = true; return; }
    const prev = hist[hist.length - 1];
    if (!prev || !prev.pillars) { el.hidden = true; return; }

    const overall = index - (prev.score || 0);
    const oCls = overall > 0 ? "up" : (overall < 0 ? "down" : "flat");
    const oTxt = overall > 0 ? "+" + overall : (overall < 0 ? String(overall) : "no change");
    const daysAgo = Math.max(1, Math.round((Date.now() - (prev.ts || Date.now())) / 86400000));

    let rows = "";
    PILLARS.forEach(p => {
      const now = pillars[p];
      const was = (prev.pillars[p] != null) ? prev.pillars[p] : now;
      const d = now - was;
      const cls = d > 0 ? "up" : (d < 0 ? "down" : "flat");
      const txt = d > 0 ? "+" + d : (d < 0 ? String(d) : "±0");
      rows +=
        '<div class="tpi-diff-row">' +
          '<span class="pillar-name"><span class="pillar-dot" style="background:' + PILLAR_META[p].dot + ';"></span>' + PILLAR_META[p].label + "</span>" +
          '<span class="tpi-diff-minitrack">' +
            '<span class="tpi-diff-prev" style="width:' + was + '%;"></span>' +
            '<span class="tpi-diff-now" style="width:' + now + '%;"></span>' +
          "</span>" +
          '<span class="tpi-diff-delta ' + cls + '">' + txt + "</span>" +
        "</div>";
    });

    el.hidden = false;
    el.innerHTML =
      '<div class="tpi-diff-head">' +
        "<h3>Your progress since last check-in</h3>" +
        '<span class="tpi-diff-overall">TPI <span class="' + oCls + '">' + oTxt + "</span> · " + daysAgo + "d ago</span>" +
      "</div>" +
      '<div class="tpi-diff-rows">' + rows + "</div>";
  }

  function renderBlueprint() {
    const pillars = computePillars();
    const index = computeIndex(pillars);
    const g = grade(index);
    const built = buildPlan(pillars);
    const goals = planToGoals(built.plan);

    RESULT = { pillars: pillars, index: index, plan: built.plan, focus: built.focus, goals: goals, tier: tierOf(index), route: routeOf(index) };

    // Tier badge
    const badge = root.querySelector("#tpi-tier-badge");
    const badgeLabel = root.querySelector("#tpi-tier-label");
    if (badge && badgeLabel) { badgeLabel.innerText = RESULT.tier.label; badge.style.display = "inline-flex"; }

    // Empathy summary
    const goalLabel = { recovery: "recovery", sleep: "deep sleep", performance: "performance", longevity: "longevity" }[answers.goal] || "recovery";
    const focusLabels = built.focus.map(p => PILLAR_META[p].label).join(" and ");
    const summaryEl = root.querySelector("#empathy-summary");
    if (summaryEl) {
      summaryEl.innerText = "Your answers produce a Total Performance Index of " + index + " — " + RESULT.tier.label +
        " — weighted around your goal of " + goalLabel + ". Your biggest leverage right now is in " + focusLabels +
        " — so your protocol below front-loads exactly those systems.";
    }

    renderRadar(pillars);
    renderOffer(RESULT);
    renderBenchmark(index);
    renderDiff(pillars, index);

    // Progress feedback: show delta vs the previous assessment (Phase 3).
    if (window.EWHQ) {
      const hist = window.EWHQ.getPref("tpi_history") || [];
      if (hist.length && summaryEl) {
        const prev = hist[hist.length - 1];
        const delta = index - prev.score;
        if (delta !== 0) {
          summaryEl.innerText += " Since your last assessment your TPI has " +
            (delta > 0 ? "improved by " + delta : "dropped by " + Math.abs(delta)) + " points.";
        }
      }
    }

    // Score ring + grade
    root.querySelector("#score-grade").innerText = g.grade;
    root.querySelector("#score-caption").innerText = g.tone;
    const ring = root.querySelector("#overall-ring");
    const num = root.querySelector("#overall-score");
    const circ = 2 * Math.PI * 78;
    setTimeout(() => {
      if (ring) { ring.style.strokeDasharray = circ; ring.style.strokeDashoffset = circ * (1 - index / 100); }
      if (num) {
        let c = 0; const t = setInterval(() => { c += 2; if (c >= index) { c = index; clearInterval(t); } num.innerText = c; }, 24);
      }
    }, 200);

    // Pillar bars
    const list = root.querySelector("#pillar-list");
    if (list) {
      const ordered = PILLARS.slice().sort((a, b) => pillars[b] - pillars[a]);
      list.innerHTML = ordered.map(p => {
        const meta = PILLAR_META[p];
        const isFocus = built.focus.indexOf(p) !== -1;
        return '<div class="pillar-row' + (isFocus ? " is-focus" : "") + '">' +
          '<span class="pillar-name"><span class="pillar-dot" style="background:' + meta.dot + ';"></span>' + meta.label + "</span>" +
          '<span class="pillar-bar-track"><span class="pillar-bar-fill" data-w="' + pillars[p] + '" style="background:' + meta.color + ';"></span></span>' +
          '<span class="pillar-score">' + pillars[p] + "</span></div>";
      }).join("");
      requestAnimationFrame(() => {
        list.querySelectorAll(".pillar-bar-fill").forEach(bar => { bar.style.width = bar.dataset.w + "%"; });
      });
    }

    // Plan cards
    const planLead = root.querySelector("#plan-lead");
    if (planLead) {
      planLead.innerText = "Log these in your Wellness Hub each week. We've set your hub goals to " +
        goals.sessions + " sessions and " + goals.recovery_minutes + " recovery minutes to match.";
    }
    const planGrid = root.querySelector("#plan-grid");
    if (planGrid) {
      planGrid.innerHTML = built.plan.map(p =>
        '<div class="plan-card">' +
        '<div class="plan-card-top"><span class="plan-emoji">' + p.emoji + '</span><span class="plan-modality">' + p.label + "</span></div>" +
        '<div class="plan-dose">' + p.sessions + "×/week · " + p.minutes + " min</div>" +
        '<p class="plan-why">' + p.why + "</p></div>"
      ).join("");
    }

    persist();
    showStep("results");

    // Lock the intake to the checkout payload: raw answers + computed TPI.
    // These attributes attach to any order placed in this session, feeding
    // the anonymized performance-data loop (Phase 4).
    const intake = Object.assign({}, answers);
    delete intake.contact_name; delete intake.contact_email; delete intake.contact_consent;
    fetch(shopRoot + "cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify({ attributes: {
        Wellness_Blueprint_Data: JSON.stringify(intake),
        TPI_Score: String(RESULT.index),
        TPI_Tier: RESULT.tier.key,
        TPI_Route: RESULT.route,
        TPI_AB_Variant: CFG.abEnabled ? AB_VARIANT : ""
      } })
    }).catch(() => {});

    // Progress saved to a completed assessment — clear the resume checkpoint.
    if (window.EWHQ) window.EWHQ.clearPartial();
  }

  function persist() {
    if (!RESULT) return;
    const p = RESULT.pillars;
    const payload = {
      // raw inputs
      goal: answers.goal, exertion: answers.exertion, sleep_hours: answers.sleep_hours,
      sleep_quality: answers.sleep_quality, stress: answers.stress, energy: answers.energy,
      modalities: answers.modalities, age: answers.age,
      environment: answers.environment, scope: answers.scope,
      // rich computed results
      wellness_index: RESULT.index,
      tpi_score: RESULT.index,
      tpi_tier: RESULT.tier.key,
      tpi_tier_label: RESULT.tier.label,
      tpi_route: RESULT.route,
      pillars: p,
      focus_areas: RESULT.focus,
      recommended_plan: RESULT.plan,
      recommended_goals: RESULT.goals,
      // legacy fields the Wellness Hub EUS block already reads (now backed by real data)
      computed_score: RESULT.index,
      status_headline: grade(RESULT.index).grade,
      status_description: "Blueprint optimized for a " +
        (answers.environment === "commercial" ? "commercial" : "private residence") + " build.",
      nervous_system: (p.stress >= 70) ? "Parasympathetic Dominant" : "Sympathetic Load",
      consistency: answers.modalities === "regularly" ? "Established Practice" : answers.modalities === "sometimes" ? "Building Consistency" : "Just Starting",
      sleep_architecture: p.sleep,
      recovery_threshold: p.recovery,
      system_resilience: Math.round((p.stress + p.energy) / 2),
      cellular_longevity: p.longevity,
      thermal_volume: RESULT.plan.filter(x => x.type === "sauna" || x.type === "red_light").reduce((n, x) => n + x.sessions * x.minutes, 0),
      cryo_volume: RESULT.plan.filter(x => x.type === "cold_plunge").reduce((n, x) => n + x.sessions * x.minutes, 0),
      completed_at: Math.floor(Date.now() / 1000)
    };

    const note = root.querySelector("#results-saved-note");
    if (window.EWHQ) {
      window.EWHQ.setAssessment(payload);
      window.EWHQ.setGoals(RESULT.goals);            // personalize the hub's activity rings
      window.EWHQ.setPref("focus", RESULT.focus);    // weakest pillars
      window.EWHQ.setPref("plan", RESULT.plan);      // full protocol for the hub blueprint panel

      // TPI history: powers score deltas + the hub's progress trend (Phase 3).
      const hist = window.EWHQ.getPref("tpi_history") || [];
      hist.push({ ts: Date.now(), score: RESULT.index, tier: RESULT.tier.key, pillars: p });
      window.EWHQ.setPref("tpi_history", hist.slice(-24));
      if (note) note.innerText = window.EWHQ.isBackendConnected
        ? "✓ Saved to your account — synced across devices."
        : "✓ Saved to your Wellness Hub on this device.";
    } else {
      try { localStorage.setItem("ehq_assessment_" + (CFG.customerId || "0"), JSON.stringify(payload)); } catch (e) {}
      if (note) note.innerText = "✓ Saved to your Wellness Hub on this device.";
    }

    syncToCrm(payload);
  }

  /* ================================================================
     CRM SYNC (Klaviyo)
     Subscribes the lead with their full TPI profile so flows can
     route them onto the right offer sequence automatically.
     ================================================================ */
  function syncToCrm(payload) {
    if (!answers.contact_email || !answers.contact_consent) return;
    if (!CFG.klaviyoPublicKey) return;
    const p = payload.pillars || {};
    const props = {
      tpi_score: payload.tpi_score,
      tpi_tier: payload.tpi_tier,
      tpi_route: payload.tpi_route,
      tpi_goal: answers.goal,
      tpi_environment: answers.environment,
      tpi_scope: answers.scope,
      tpi_focus_areas: (payload.focus_areas || []).join(","),
      tpi_recovery: p.recovery, tpi_sleep: p.sleep, tpi_stress: p.stress,
      tpi_energy: p.energy, tpi_longevity: p.longevity,
      tpi_ab_variant: CFG.abEnabled ? AB_VARIANT : undefined,
      tpi_completed_at: new Date().toISOString()
    };

    // 1. Subscribe to the list with the full TPI profile. Include phone +
    //    SMS consent so a text channel/flow can be triggered too.
    if (CFG.klaviyoListId) {
      const subAttrs = {
        list_id: CFG.klaviyoListId,
        custom_source: "TPI Wellness Assessment",
        email: answers.contact_email,
        properties: Object.assign({ first_name: answers.contact_name || undefined }, props)
      };
      if (answers.contact_sms_consent && answers.contact_phone) {
        subAttrs.phone_number = answers.contact_phone;
        subAttrs.consented_channels = ["email", "sms"];
      }
      const sub = { data: { type: "subscription", attributes: subAttrs } };
      fetch("https://a.klaviyo.com/client/subscriptions/?company_id=" + CFG.klaviyoPublicKey, {
        method: "POST",
        headers: { "Content-Type": "application/json", "revision": "2023-02-22" },
        body: JSON.stringify(sub)
      }).catch(() => {});
    }

    // 2. Fire a metric event so flows can trigger off "TPI Assessment
    //    Completed" and branch on tier/route immediately.
    const evt = {
      data: {
        type: "event",
        attributes: {
          metric: { name: "TPI Assessment Completed" },
          profile: {
            email: answers.contact_email,
            first_name: answers.contact_name || undefined
          },
          properties: props,
          time: new Date().toISOString()
        }
      }
    };
    fetch("https://a.klaviyo.com/client/events/?company_id=" + CFG.klaviyoPublicKey, {
      method: "POST",
      headers: { "Content-Type": "application/json", "revision": "2023-02-22" },
      body: JSON.stringify(evt)
    }).catch(() => {});
  }

  /* ================================================================
     ECOSYSTEM REVEAL + BUILD (commercial funnel — preserved)
     ================================================================ */
  const viewBtn = root.querySelector("#view-ecosystem-btn");
  if (viewBtn) {
    viewBtn.addEventListener("click", (e) => {
      e.preventDefault();
      const targetGoal = answers.goal || "recovery";
      root.querySelectorAll(".hardware-card").forEach(card => {
        const goalMap = card.dataset.goalMap;
        card.style.display = (goalMap === "all" || goalMap === targetGoal) ? "flex" : "none";
      });
      const expanded = root.querySelector("#blueprint-expanded-results");
      expanded.style.display = "block";
      void expanded.offsetWidth;
      expanded.classList.add("is-visible");
      renderBundle();
      setTimeout(() => {
        expanded.scrollIntoView({ behavior: "smooth", block: "start" });
        root.querySelectorAll(".bar").forEach(bar => { bar.style.height = bar.dataset.height; });
      }, 300);
    });
  }

  /* ================================================================
     BUNDLE PRICING — goal-based kit total + savings
     Reads the visible hardware cards (already goal-filtered) and shows a
     transparent kit price with a modeled bundle saving.
     ================================================================ */
  const BUNDLE_DISCOUNT = 0.1; // 10% modeled ecosystem/bundle saving
  function renderBundle() {
    const wrap = root.querySelector("#tpi-bundle");
    const body = root.querySelector("#tpi-bundle-body");
    if (!wrap || !body) return;
    const items = [];
    root.querySelectorAll(".hardware-card").forEach(card => {
      if (card.style.display !== "none" && card.dataset.price) {
        items.push({ title: card.dataset.title || "System component", price: parseInt(card.dataset.price, 10) || 0 });
      }
    });
    if (items.length < 1) { wrap.hidden = true; return; }
    const subtotal = items.reduce((n, i) => n + i.price, 0);
    const save = items.length > 1 ? Math.round(subtotal * BUNDLE_DISCOUNT) : 0;
    const total = subtotal - save;

    let rows = items.map(i =>
      '<div class="tpi-bundle-row"><span>' + i.title + "</span><span>" + formatMoney(i.price) + "</span></div>"
    ).join("");
    if (save > 0) {
      rows += '<div class="tpi-bundle-row"><span>Ecosystem bundle saving</span><span class="save">− ' + formatMoney(save) + "</span></div>";
    }
    rows += '<div class="tpi-bundle-row total"><span>Your kit total</span><span>' + formatMoney(total) + "</span></div>";
    body.innerHTML = rows;
    wrap.hidden = false;
    body.insertAdjacentHTML("beforeend",
      '<p class="tpi-bundle-note">Bundle pricing is applied automatically when you build the full system together. Financing available at checkout.</p>');
  }

  /* ================================================================
     SHAREABLE SCORECARD (canvas → PNG) — free social proof
     ================================================================ */
  function drawScorecard() {
    const canvas = root.querySelector("#tpi-scorecard-canvas");
    if (!canvas || !RESULT) return null;
    const ctx = canvas.getContext("2d");
    const W = canvas.width, H = canvas.height;

    // Background gradient
    const bg = ctx.createLinearGradient(0, 0, 0, H);
    bg.addColorStop(0, "#0a0a0f");
    bg.addColorStop(1, "#141420");
    ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);

    const accent = "#0071e3";
    ctx.textAlign = "center";

    // Brand
    ctx.fillStyle = "rgba(255,255,255,0.6)";
    ctx.font = "600 34px -apple-system, Segoe UI, Roboto, sans-serif";
    ctx.fillText("ELITE WELLNESS HQ", W / 2, 130);

    ctx.fillStyle = "#ffffff";
    ctx.font = "700 58px -apple-system, Segoe UI, Roboto, sans-serif";
    ctx.fillText("Total Performance Index", W / 2, 210);

    // Big score ring
    const cx = W / 2, cy = 520, r = 200;
    ctx.lineWidth = 30;
    ctx.strokeStyle = "rgba(255,255,255,0.1)";
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.stroke();
    ctx.strokeStyle = accent;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.arc(cx, cy, r, -Math.PI / 2, -Math.PI / 2 + (Math.PI * 2 * RESULT.index / 100));
    ctx.stroke();
    ctx.fillStyle = "#ffffff";
    ctx.font = "200 180px -apple-system, Segoe UI, Roboto, sans-serif";
    ctx.fillText(String(RESULT.index), cx, cy + 55);
    ctx.fillStyle = "rgba(255,255,255,0.55)";
    ctx.font = "600 30px -apple-system, Segoe UI, Roboto, sans-serif";
    ctx.fillText("OUT OF 100", cx, cy + 110);

    // Tier
    ctx.fillStyle = accent;
    ctx.font = "700 44px -apple-system, Segoe UI, Roboto, sans-serif";
    ctx.fillText(RESULT.tier.label.toUpperCase(), W / 2, 830);

    // Pillars
    let y = 940;
    ctx.textAlign = "left";
    PILLARS.forEach(p => {
      const meta = PILLAR_META[p];
      const val = RESULT.pillars[p];
      ctx.fillStyle = "rgba(255,255,255,0.85)";
      ctx.font = "500 30px -apple-system, Segoe UI, Roboto, sans-serif";
      ctx.fillText(meta.label, 160, y + 26);
      // track
      const tx = 520, tw = 320, th = 20;
      ctx.fillStyle = "rgba(255,255,255,0.1)";
      ctx.fillRect(tx, y + 8, tw, th);
      ctx.fillStyle = meta.color;
      ctx.fillRect(tx, y + 8, tw * (val / 100), th);
      ctx.fillStyle = "#ffffff";
      ctx.textAlign = "right";
      ctx.font = "700 30px monospace";
      ctx.fillText(String(val), W - 160, y + 30);
      ctx.textAlign = "left";
      y += 72;
    });

    // Footer
    ctx.textAlign = "center";
    ctx.fillStyle = "rgba(255,255,255,0.5)";
    ctx.font = "500 28px -apple-system, Segoe UI, Roboto, sans-serif";
    ctx.fillText("Take your free assessment at elitewellnesshq.com", W / 2, H - 70);
    return canvas;
  }

  const scorecardBtn = root.querySelector("#download-scorecard-btn");
  if (scorecardBtn) {
    scorecardBtn.addEventListener("click", () => {
      const canvas = drawScorecard();
      if (!canvas) return;
      canvas.toBlob(blob => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        // Try native share (mobile) with the image, fall back to download.
        const file = new File([blob], "my-tpi-scorecard.png", { type: "image/png" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          navigator.share({ files: [file], title: "My Total Performance Index", text: "My TPI is " + RESULT.index + " — " + RESULT.tier.label }).catch(() => {});
          setTimeout(() => URL.revokeObjectURL(url), 4000);
          return;
        }
        const a = document.createElement("a");
        a.href = url; a.download = "my-tpi-scorecard.png";
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 4000);
      }, "image/png");
    });
  }

  /* ================================================================
     PRINTABLE / PDF BLUEPRINT
     ================================================================ */
  const printBtn = root.querySelector("#print-blueprint-btn");
  if (printBtn) {
    printBtn.addEventListener("click", () => {
      if (!RESULT) return;
      const pillarsHtml = PILLARS.slice().sort((a, b) => RESULT.pillars[b] - RESULT.pillars[a]).map(p =>
        '<tr><td>' + PILLAR_META[p].label + '</td><td style="text-align:right;font-weight:700;">' + RESULT.pillars[p] + '</td></tr>'
      ).join("");
      const planHtml = RESULT.plan.map(p =>
        '<li><strong>' + p.label + '</strong> — ' + p.sessions + '×/week · ' + p.minutes + ' min. ' + p.why + '</li>'
      ).join("");
      const w = window.open("", "_blank");
      if (!w) return;
      w.document.write(
        '<!doctype html><html><head><meta charset="utf-8"><title>My TPI Blueprint</title>' +
        '<style>body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;color:#111;max-width:760px;margin:40px auto;padding:0 24px;line-height:1.55;}' +
        'h1{font-size:28px;margin:0 0 4px;}h2{font-size:18px;margin:28px 0 10px;border-bottom:2px solid #0071e3;padding-bottom:6px;}' +
        '.eyebrow{color:#0071e3;font-weight:700;letter-spacing:.1em;text-transform:uppercase;font-size:12px;}' +
        '.score{font-size:64px;font-weight:200;color:#0071e3;line-height:1;}' +
        'table{width:100%;border-collapse:collapse;}td{padding:8px 0;border-bottom:1px solid #eee;}' +
        'ul{padding-left:18px;}li{margin-bottom:10px;}' +
        '.tier{display:inline-block;background:#0071e3;color:#fff;padding:4px 12px;border-radius:99px;font-size:13px;font-weight:600;margin-top:8px;}' +
        '@media print{.noprint{display:none;}}</style></head><body>' +
        '<div class="eyebrow">Elite Wellness HQ</div>' +
        '<h1>Your Total Performance Index</h1>' +
        '<div class="score">' + RESULT.index + '<span style="font-size:24px;color:#999;">/100</span></div>' +
        '<div class="tier">' + RESULT.tier.label + '</div>' +
        '<h2>Performance Domains</h2><table>' + pillarsHtml + '</table>' +
        '<h2>Your Personalized Weekly Protocol</h2><ul>' + planHtml + '</ul>' +
        '<p style="margin-top:32px;color:#666;font-size:13px;">Generated at elitewellnesshq.com · Log your protocol in your Wellness Hub to track progress over time.</p>' +
        '<button class="noprint" onclick="window.print()" style="margin-top:20px;background:#0071e3;color:#fff;border:none;padding:12px 24px;border-radius:8px;font-size:15px;cursor:pointer;">Print / Save as PDF</button>' +
        '</body></html>');
      w.document.close();
      setTimeout(() => { try { w.focus(); w.print(); } catch (e) {} }, 500);
    });
  }

  const buildBtn = root.querySelector("#initialize-build-btn");
  if (buildBtn) {
    buildBtn.addEventListener("click", function () {
      this.innerText = "Calculating System Integration...";
      this.style.opacity = "0.8";
      this.style.pointerEvents = "none";

      const itemsToAdd = [];
      root.querySelectorAll(".hardware-card").forEach(card => {
        if (card.style.display !== "none" && card.dataset.variantId) {
          const vid = parseInt(card.dataset.variantId, 10);
          if (!isNaN(vid)) itemsToAdd.push({ id: vid, quantity: 1 });
        }
      });
      const targetHubUrl = CFG.hubUrl || "/pages/wellness-hub";
      if (itemsToAdd.length > 0) {
        fetch(shopRoot + "cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Accept": "application/json" },
          body: JSON.stringify({ items: itemsToAdd })
        }).then(r => r.json()).then(() => { window.location.href = targetHubUrl; })
          .catch(() => { window.location.href = targetHubUrl; });
      } else {
        window.location.href = targetHubUrl;
      }
    });
  }

  updateChrome(currentStep);

  // Offer to resume an in-progress assessment once the data layer is ready.
  if (window.EWHQ) { offerResume(); }
  else { document.addEventListener("ewhq:ready", offerResume, { once: true }); }
});
